/** A Management section that can be folded away, and remembers that it was.
 *
 * ⛔ WHY. The Management page is eight tall cards. Jacob went looking for the
 * Keywords section, could not find it, and reasonably concluded the app was
 * missing the feature — it was four cards below the fold, under a
 * Conversion actions table long enough to read as the end of the page. A screen
 * where a section is present but unfindable is not meaningfully different from
 * one where it is absent.
 *
 * ⚠ DEFAULTS OPEN. Collapsing by default would trade "cannot find it" for
 * "did not know it was there", which is the same failure wearing a tidier
 * layout. The operator folds away what they do not use; nothing hides itself.
 *
 * ⚠ THE HEADING STAYS VISIBLE WHEN COLLAPSED, and so does the row count. A
 * collapsed section still tells you it exists and roughly what is in it, so
 * folding one away never costs you the knowledge that it has 594 keywords in
 * it.
 */
import { useCallback, useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { OPEN_SECTION_EVENT } from '@/components/google-ads/managementScroll';
import { cn } from '@/lib/utils';

const STORAGE_PREFIX = 'ga-mgmt-card:';

function readStored(id: string): boolean | null {
  // ⚠ Wrapped: a private window, cleared site data, or a browser configured to
  // block storage makes the ACCESSOR itself throw, not just return null. An
  // unguarded read here would blank the whole Management page.
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + id);
    return raw === null ? null : raw === 'open';
  } catch {
    return null;
  }
}

export function CollapsibleCard({
  id,
  title,
  description,
  /** Shown beside the title, and kept visible while collapsed. */
  summary,
  children,
}: {
  id: string;
  title: string;
  description?: React.ReactNode;
  summary?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);

  // Read on mount rather than in useState's initialiser: the initialiser also
  // runs during SSR/prerender where `window` may not exist, and a first paint
  // that disagrees with storage would flash the section open then shut.
  useEffect(() => {
    const stored = readStored(id);
    if (stored !== null) setOpen(stored);
  }, [id]);

  useEffect(() => {
    const onOpen = (event: Event) => {
      const wanted = (event as CustomEvent<{ id?: string }>).detail?.id;
      if (wanted !== id) return;
      setOpen(true);
      try {
        window.localStorage.setItem(STORAGE_PREFIX + id, 'open');
      } catch {
        // Same as toggle: failing to save must not block opening.
      }
    };
    window.addEventListener(OPEN_SECTION_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SECTION_EVENT, onOpen);
  }, [id]);

  const toggle = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(STORAGE_PREFIX + id, next ? 'open' : 'closed');
      } catch {
        // Preference is a convenience; failing to save it must never break
        // the toggle itself.
      }
      return next;
    });
  }, [id]);

  return (
    <Card id={id}>
      <CardHeader className="py-3">
        {/* The whole header is the control, so the target is the full width of
            the card rather than a 16px chevron — this is used on a phone. */}
        <button
          type="button"
          onClick={toggle}
          aria-expanded={open}
          aria-controls={`${id}-content`}
          className="flex w-full items-start gap-2 text-left"
        >
          <ChevronDown
            className={cn(
              'mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              !open && '-rotate-90',
            )}
          />
          <span className="min-w-0 flex-1">
            <CardTitle className="text-base">
              {title}
              {summary ? (
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  {summary}
                </span>
              ) : null}
            </CardTitle>
            {/* The description explains what the section covers and what it
                deliberately excludes; it is noise once folded away. */}
            {open && description ? (
              <CardDescription className="mt-1">{description}</CardDescription>
            ) : null}
          </span>
        </button>
      </CardHeader>
      {open ? (
        <CardContent id={`${id}-content`}>{children}</CardContent>
      ) : null}
    </Card>
  );
}

export default CollapsibleCard;
